"use client";

import { useArticleAudio } from "@/context/article-audio-context";
import { createImageSizes } from "@/utils/image";
import Image from "next/image";
import Link from "next/link";
import { ReactEventHandler, useEffect, useRef, useState } from "react";
import Icon from "./icon";
import { formatSeconds } from "./podcast-player";

/**
 * Flytande spelare för uppläst artikelljud, längst ner på skärmen - samma
 * mönster som PodcastPlayer (se den filen), men utan podd-specifika delar
 * (visningsbild, prenumerationslänkar). Styrs av ArticleAudioContext,
 * triggas av lyssna-knappen i meta.tsx.
 */
const ArticleAudioPlayer = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const { audio, closeArticleAudio } = useArticleAudio();

  const onClose = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    if (audioRef.current) {
      audioRef.current.src = "";
    }

    closeArticleAudio();
  };

  useEffect(() => {
    setIsPlaying(true);
    setCurrentTime(0);
    setDuration(0);

    if (audioRef.current && audio?.audioUrl) {
      audioRef.current.src = audio.audioUrl;
      audioRef.current.play();
    }
  }, [audio]);

  if (!audio) {
    return null;
  }

  const onPlay = () => {
    audioRef.current?.play();
    setIsPlaying(true);
  };

  const onPause = () => {
    audioRef.current?.pause();
    setIsPlaying(false);
  };

  const onTimeUpdate: ReactEventHandler<HTMLAudioElement> = (event) => {
    setCurrentTime((event.target as HTMLAudioElement).currentTime);
  };

  const onEnded = () => {
    setIsPlaying(false);
  };

  const onRangeChange: ReactEventHandler<HTMLInputElement> = (event) => {
    const updatedCurrentTime = Number((event.target as HTMLInputElement).value);

    if (audioRef.current) {
      audioRef.current.currentTime = updatedCurrentTime;
    }

    setCurrentTime(updatedCurrentTime);
  };

  const onLoadedMetadata: ReactEventHandler<HTMLAudioElement> = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-10 bg-sand-700 border-t-[5px] border-sand-700">
        <div className="container pt-4 pb-8 md:pb-4 lg:p-8 flex justify-between space-x-4 lg:space-x-8">
          <div className="flex flex-1">
            {audio.imageUrl && (
              <div className="relative w-[80px] h-[80px] flex-shrink-0 mr-4 lg:mr-8 hidden lg:block">
                <Image
                  fill
                  src={audio.imageUrl}
                  alt=""
                  className="rounded-sm object-cover"
                  sizes={createImageSizes({
                    mobile: "12vw",
                    tablet: "12vw",
                    desktop: "12vw",
                  })}
                />
              </div>
            )}

            <div className="flex-1">
              <div className="uppercase text-sm text-gray-400 font-bold mb-1">
                Uppläst artikel
              </div>
              <Link href={audio.articleUrl}>
                <h2 className="font-semibold text-lg lg:text-2xl line-clamp-2 hover:underline">
                  {audio.title}
                </h2>
              </Link>

              <div className="flex items-center mt-2">
                {isPlaying ? (
                  <button onClick={onPause} aria-label="Pausa uppläsning">
                    <Icon.PauseInverted
                      bgClassName="fill-red-800"
                      className="fill-white-500 w-7 h-7"
                    />
                  </button>
                ) : (
                  <button onClick={onPlay} aria-label="Spela upp">
                    <Icon.PlayInverted
                      bgClassName="fill-red-800"
                      className="fill-white-500 w-7 h-7"
                    />
                  </button>
                )}

                <div className="text-sm text-gray-400 ml-2 min-w-[50px] text-center">
                  {formatSeconds(currentTime)}
                </div>

                <input
                  value={currentTime}
                  type="range"
                  max={duration}
                  className="range-thumb-red w-full max-w-[520px] h-[2px] bg-gray-300 appearance-none cursor-pointer"
                  onChange={onRangeChange}
                  aria-label="Sök i uppläsningen"
                />

                <div className="text-sm text-gray-400 min-w-[50px] text-center">
                  {formatSeconds(duration)}
                </div>
              </div>
            </div>
          </div>

          <button
            aria-label="Stäng uppläsning"
            className="btn-icon bg-sand-800 lg:h-10 lg:w-10 mb-auto"
            onClick={onClose}
          >
            <Icon.Close className="fill-gray-300 h-4" />
          </button>
        </div>
      </div>

      <audio
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        className="hidden"
        ref={audioRef}
        onLoadedMetadata={onLoadedMetadata}
      />
    </>
  );
};

export default ArticleAudioPlayer;
